import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { App as CapApp } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { toast } from "sonner";

/** Screens where the hardware back button triggers the double-tap-to-exit flow. */
const ROOT_SCREENS: ReadonlySet<string> = new Set([
  "/player/home",
  "/scout/find",
  "/academy/home",
  "/role-select",
  "/auth",
]);

/** Screens that have their own custom back-button handler registered. */
const PAGE_OVERRIDES: ReadonlySet<string> = new Set([
  "/player/strength-card",
  "/player/reports",
  "/player/videos",
]);

const EXIT_CONFIRM_WINDOW_MS = 2000;

// --- Custom exit-dialog subscription ---------------------------------------
// ExitConfirmDialog registers a listener at mount; this hook calls it when the
// user taps back twice on a root screen. Kept as a module-level single slot
// because there's only ever one ExitConfirmDialog in the tree.
let exitRequestListener: (() => void) | null = null;

export function registerExitRequestListener(fn: () => void): () => void {
  exitRequestListener = fn;
  return () => {
    if (exitRequestListener === fn) exitRequestListener = null;
  };
}

async function requestExit() {
  if (exitRequestListener) {
    exitRequestListener();
    return;
  }
  // Fallback if the dialog hasn't mounted — still close the app.
  await CapApp.exitApp().catch(() => { /* ignore */ });
}

/**
 * Global hardware-back-button handler for the Capacitor shell.
 *
 *  - On root screens (ROOT_SCREENS): first press shows a toast "Press back
 *    again to exit"; a second press inside EXIT_CONFIRM_WINDOW_MS opens the
 *    in-app ExitConfirmDialog.
 *  - On pages with their own listener (PAGE_OVERRIDES): we skip, so the page
 *    effect handles it.
 *  - Everywhere else: single press calls react-router navigate(-1).
 *
 * No-op in web (browser) — Capacitor's backButton event is native-only.
 */
export function useGlobalBackButton() {
  const navigate = useNavigate();
  const location = useLocation();
  const locationRef = useRef(location);
  const lastBackAt = useRef(0);

  useEffect(() => {
    locationRef.current = location;
  }, [location]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let remove: (() => void) | undefined;
    let cancelled = false;

    (async () => {
      const handle = await CapApp.addListener("backButton", async () => {
        const path = locationRef.current.pathname;

        if (PAGE_OVERRIDES.has(path)) return;

        if (ROOT_SCREENS.has(path)) {
          const now = Date.now();
          if (now - lastBackAt.current < EXIT_CONFIRM_WINDOW_MS) {
            lastBackAt.current = 0;
            await requestExit();
            return;
          }
          lastBackAt.current = now;
          toast("Press back again to exit", { duration: EXIT_CONFIRM_WINDOW_MS });
          return;
        }

        // react-router navigate(-1) plays nicer with Capacitor than window.history.back()
        // (the latter sometimes no-ops inside the Android WebView when a lazy
        // route is still resolving).
        navigate(-1);
      });

      if (cancelled) {
        void handle.remove();
        return;
      }
      remove = () => void handle.remove();
    })();

    return () => {
      cancelled = true;
      remove?.();
    };
  }, [navigate]);
}
